/**
 * Mock data for local development testing of AgentMonitor.
 * Simulates a realistic agent event flow with sub-agents and tools.
 *
 * Usage: import { MOCK_TOOLS, MOCK_PROMPT_CONFIG, useMockAgentEvents } from "./mock-data";
 */
import { useState, useEffect, useRef } from "react";

export const MOCK_PROMPT_CONFIG = {
  globalPrompt: "You are a helpful travel assistant that helps users book flights and find hotels.",
  personality: "friendly, helpful, efficient",
  tone: "warm, concise, professional",
};

export const MOCK_TOOLS = [
  // Sub-agent 1: research_agent (3 child tools)
  {
    id: "tool-1",
    name: "research_agent",
    type: "sub_agent",
    description: "Research agent that finds best deals",
    toolData: {
      tools: {
        search_hotels: {
          name: "search_hotels",
          type: "function",
          description: "Search for hotels",
          parameters: { city: { type: "string", required: true } },
        },
        compare_prices: {
          name: "compare_prices",
          type: "function",
          description: "Compare prices across providers",
          parameters: { items: { type: "array", required: true } },
        },
        read_reviews: {
          name: "read_reviews",
          type: "function",
          description: "Read user reviews for a listing",
          parameters: { listingId: { type: "string", required: true } },
        },
      },
    },
  },
  // Sub-agent 2: booking_agent (2 child tools)
  {
    id: "tool-2",
    name: "booking_agent",
    type: "sub_agent",
    description: "Handles reservations and bookings",
    toolData: {
      tools: {
        reserve_room: {
          name: "reserve_room",
          type: "function",
          description: "Reserve a hotel room",
          parameters: { hotelId: { type: "string", required: true }, dates: { type: "string", required: true } },
        },
        cancel_reservation: {
          name: "cancel_reservation",
          type: "function",
          description: "Cancel an existing reservation",
          parameters: { reservationId: { type: "string", required: true } },
        },
      },
    },
  },
  // Regular tools (5)
  {
    id: "tool-3",
    name: "book_flight",
    type: "function",
    description: "Book a flight to a destination",
    parameters: {
      destination: { type: "string", required: true },
      date: { type: "string", required: false },
    },
  },
  {
    id: "tool-4",
    name: "check_weather",
    type: "function",
    description: "Check weather for a location",
    parameters: {
      location: { type: "string", required: true },
    },
  },
  {
    id: "tool-5",
    name: "send_email",
    type: "function",
    description: "Send confirmation email",
    parameters: {
      to: { type: "string", required: true },
      subject: { type: "string", required: true },
    },
  },
  {
    id: "tool-6",
    name: "get_exchange_rate",
    type: "function",
    description: "Get currency exchange rate",
    parameters: {
      from: { type: "string", required: true },
      to: { type: "string", required: true },
    },
  },
  {
    id: "tool-7",
    name: "translate_text",
    type: "function",
    description: "Translate text to another language",
    parameters: {
      text: { type: "string", required: true },
      targetLang: { type: "string", required: true },
    },
  },
];

const now = Date.now();

const MOCK_EVENTS = [
  // === Phase 1: Agent loop (workflowRunning=false) — shows AgentMonitor ===
  {
    eventId: "evt-001",
    type: "input_message",
    timestamp: now,
    isSubAgent: false,
    workflowRunning: false,
    payload: { message: "I want to book a trip to London" },
  },
  {
    eventId: "evt-002",
    type: "llm_request",
    timestamp: now + 100,
    isSubAgent: false,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      messages: [
        { role: "system", content: "You are a helpful travel assistant..." },
        { role: "user", content: "I want to book a trip to London" },
      ],
      tools: [
        { type: "function", function: { name: "book_flight" } },
        { type: "function", function: { name: "check_weather" } },
        { type: "function", function: { name: "research_agent" } },
      ],
    },
  },
  {
    eventId: "evt-003",
    type: "llm_response",
    timestamp: now + 2500,
    isSubAgent: false,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      content: null,
      tool_calls: [
        { id: "call_weather", type: "function", function: { name: "check_weather", arguments: '{"location":"London"}' } },
      ],
      role: "assistant",
    },
  },
  {
    eventId: "evt-004",
    type: "tool_start",
    timestamp: now + 2600,
    isSubAgent: false,
    workflowRunning: false,
    payload: { toolName: "check_weather", toolCallId: "call_weather", input: { location: "London" } },
  },
  {
    eventId: "evt-005",
    type: "tool_end",
    timestamp: now + 3200,
    isSubAgent: false,
    workflowRunning: false,
    payload: {
      toolName: "check_weather",
      toolCallId: "call_weather",
      input: { location: "London" },
      output: { result: "London: 12°C, partly cloudy, chance of rain 30%" },
    },
  },
  {
    eventId: "evt-006",
    type: "llm_request",
    timestamp: now + 3300,
    isSubAgent: false,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      messages: [
        { role: "user", content: "I want to book a trip to London" },
        { role: "tool", content: "London: 12°C, partly cloudy" },
      ],
    },
  },
  {
    eventId: "evt-007",
    type: "llm_response",
    timestamp: now + 5000,
    isSubAgent: false,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      content: "Great choice! London is at 12°C with partly cloudy skies. Let me start the booking process for you.",
      tool_calls: null,
      role: "assistant",
    },
  },
  {
    eventId: "evt-008",
    type: "output_message",
    timestamp: now + 5100,
    isSubAgent: false,
    workflowRunning: false,
    payload: { message: "Great choice! London is at 12°C with partly cloudy skies. Let me start the booking process for you." },
  },

  // === Phase 2: Workflow kicks in (workflowRunning=true) — should switch to WorkflowDebugger ===
  {
    eventId: "evt-009",
    type: "input_message",
    timestamp: now + 10000,
    isSubAgent: false,
    workflowRunning: true,
    payload: { message: "Yes, let's go ahead" },
  },
  {
    eventId: "evt-010",
    type: "llm_request",
    timestamp: now + 10100,
    isSubAgent: false,
    workflowRunning: true,
    model: "gpt-4",
    payload: {
      messages: [
        { role: "system", content: "You are handling a booking workflow..." },
        { role: "user", content: "Yes, let's go ahead" },
      ],
    },
  },
  {
    eventId: "evt-011",
    type: "llm_response",
    timestamp: now + 12000,
    isSubAgent: false,
    workflowRunning: true,
    model: "gpt-4",
    payload: {
      content: "What date would you like to fly to London?",
      tool_calls: null,
      role: "assistant",
    },
  },
  {
    eventId: "evt-012",
    type: "output_message",
    timestamp: now + 12100,
    isSubAgent: false,
    workflowRunning: true,
    payload: { message: "What date would you like to fly to London?" },
  },
  {
    eventId: "evt-013",
    type: "interrupt_out_of_context",
    timestamp: now + 15000,
    isSubAgent: false,
    workflowRunning: true,
    payload: {
      question: "What are your business hours?",
      answer: "Our business hours are Monday to Friday, 9am to 5pm.",
    },
  },
  {
    eventId: "evt-014",
    type: "interrupt_slot_update",
    timestamp: now + 18000,
    isSubAgent: false,
    workflowRunning: true,
    payload: {
      slotsToUpdate: { destination: "Paris", travel_date: null },
      status: "waiting_for_values",
    },
  },

  // === Phase 3: Back to agent loop (workflowRunning=false) — should switch back to AgentMonitor ===
  {
    eventId: "evt-015",
    type: "input_message",
    timestamp: now + 22000,
    isSubAgent: false,
    workflowRunning: false,
    payload: { message: "Actually, can you find the cheapest hotel instead?" },
  },
  {
    eventId: "evt-016",
    type: "llm_request",
    timestamp: now + 22100,
    isSubAgent: false,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      messages: [
        { role: "user", content: "Actually, can you find the cheapest hotel instead?" },
      ],
      tools: [
        { type: "function", function: { name: "research_agent" } },
      ],
    },
  },
  {
    eventId: "evt-017",
    type: "llm_response",
    timestamp: now + 24000,
    isSubAgent: false,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      content: null,
      tool_calls: [
        { id: "call_research", type: "function", function: { name: "research_agent", arguments: '{"task":"Find cheapest hotel in London"}' } },
      ],
      role: "assistant",
    },
  },
  {
    eventId: "evt-018",
    type: "sub_agent_start",
    timestamp: now + 24100,
    isSubAgent: false,
    workflowRunning: false,
    payload: { subAgentName: "research_agent", task: "Find cheapest hotel in London" },
  },
  {
    eventId: "evt-019",
    type: "llm_request",
    timestamp: now + 24200,
    isSubAgent: true,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      messages: [
        { role: "system", content: "You are a research agent..." },
        { role: "user", content: "Find cheapest hotel in London" },
      ],
      tools: [
        { type: "function", function: { name: "search_hotels" } },
        { type: "function", function: { name: "compare_prices" } },
      ],
    },
  },
  {
    eventId: "evt-020",
    type: "llm_response",
    timestamp: now + 26000,
    isSubAgent: true,
    workflowRunning: false,
    model: "gpt-4",
    payload: {
      content: "The cheapest option is Budget Inn at $62/night.",
      tool_calls: null,
      role: "assistant",
    },
  },
  {
    eventId: "evt-021",
    type: "sub_agent_end",
    timestamp: now + 26100,
    isSubAgent: false,
    workflowRunning: false,
    payload: {
      subAgentName: "research_agent",
      task: "Find cheapest hotel in London",
      output: "The cheapest option is Budget Inn at $62/night.",
    },
  },
  {
    eventId: "evt-022",
    type: "output_message",
    timestamp: now + 27000,
    isSubAgent: false,
    workflowRunning: false,
    payload: { message: "I found Budget Inn at $62/night — that's the cheapest option in London!" },
  },
];

/**
 * Hook that drip-feeds mock agent events with realistic delays.
 * Events are emitted one by one with ~1.2s intervals to simulate real-time streaming.
 *
 * @param {boolean} enabled - Set true to activate mock mode
 * @returns {{ events: Array, tools: Array, promptConfig: object }}
 */
export function useMockAgentEvents(enabled) {
  const [events, setEvents] = useState([]);
  const indexRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    indexRef.current = 0;
    setEvents([]);

    // Start after a short initial delay
    const initialDelay = setTimeout(() => {
      // Push first event
      setEvents([MOCK_EVENTS[0]]);
      indexRef.current = 1;

      // Drip-feed remaining events
      const interval = setInterval(() => {
        if (indexRef.current >= MOCK_EVENTS.length) {
          clearInterval(interval);
          return;
        }
        setEvents((prev) => [...prev, MOCK_EVENTS[indexRef.current]]);
        indexRef.current++;
      }, 1200);

      return () => clearInterval(interval);
    }, 1500);

    return () => clearTimeout(initialDelay);
  }, [enabled]);

  return {
    events,
    tools: enabled ? MOCK_TOOLS : null,
    promptConfig: enabled ? MOCK_PROMPT_CONFIG : null,
  };
}
